import { useState, useCallback, useEffect, useRef } from 'react';
import { isEmpty, noop } from 'lodash-es';
import { isEqualById } from 'site-modules/shared/utils/appraiser/compare-utils';
import { useAppraisalTabsState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

/**
 * Custom hook encapsulating MMY vehicle selection logic.
 *
 * Handles prepopulation from `initialVehicle` on mount and reacts to
 * `vehicle` prop changes (API-resolved vehicle data from connectToModel).
 *
 * @param {Object} params
 * @param {Object} [params.initialVehicle] - Pre-selected vehicle to populate on mount.
 * @param {Object} [params.vehicle] - API-resolved vehicle object (has `id`); triggers update when changed.
 * @param {Object} [params.query] - Current MMY query (make/model/year) used when vehicle resolves.
 * @param {Function} [params.handlePrepopulatedVehicle] - Callback to notify parent of prepopulated vehicle.
 * @param {Function} [params.onVehicleChange] - Called after any dropdown change (used to reset validation).
 * @returns {{ onChange: Function, prepopulatedVehicle: Object|undefined }}
 */
export function useVehicleSelection({
  initialVehicle = null,
  vehicle = {},
  query = {},
  handlePrepopulatedVehicle = noop,
  onVehicleChange = noop,
} = {}) {
  const { setMmy } = useAppraisalTabsState();

  const [prepopulatedVehicle, setPrepopulatedVehicleState] = useState(undefined);

  const prevVehicleRef = useRef(vehicle);

  const updatePrepopulatedVehicle = useCallback(
    ({ make, model, year }) => {
      const newPrepopulatedVehicle = {
        year,
        makeSlug: make,
        modelSlug: model,
      };
      setPrepopulatedVehicleState(newPrepopulatedVehicle);
      handlePrepopulatedVehicle(newPrepopulatedVehicle);
    },
    [handlePrepopulatedVehicle]
  );

  const onChange = useCallback(
    ({ make, model, year }) => {
      setMmy({ make, model, year });
      onVehicleChange();
    },
    [setMmy, onVehicleChange]
  );

  // componentDidMount — prepopulate if initialVehicle provided
  useEffect(() => {
    if (!isEmpty(initialVehicle)) {
      onChange(initialVehicle);
      // Intentional initial form hydration from the supplied vehicle.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      updatePrepopulatedVehicle(initialVehicle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate — react to vehicle prop changes (API-resolved vehicle)
  useEffect(() => {
    if (!isEmpty(vehicle) && !isEqualById(prevVehicleRef.current, vehicle)) {
      onChange(query);
      updatePrepopulatedVehicle(query);
    }
    prevVehicleRef.current = vehicle;
  }, [vehicle, query, onChange, updatePrepopulatedVehicle]);

  return { onChange, prepopulatedVehicle };
}
